import {
  cabinRateToRateInput,
  priceCabinStay,
  type CreateItineraryComponentRequest,
  type CruiseCabin,
  type CruiseCabinRate,
  type CruiseListItem,
} from '@utazzo/shared';
import { UzBadge, UzButton } from '@utazzo/ui';
import { AlertTriangle, ChevronDown, Loader2, Ship, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { useItineraryDetail } from '../use-itineraries';

import { MealBadge } from './MealBadge';
import { AdminOnlyAmount } from './price-visibility';

import { useCruiseCoverUrl } from '@/features/cruises/use-cruise-cover';
import { useCruiseCabins, useCruiseRates, useCruisesList } from '@/features/cruises/use-cruises';
import { useFxRate } from '@/features/currencies/use-currencies';

/**
 * "Change cruise" / "Change cabin & meals" picker for a placed CRUISE stay.
 * Mirrors `PreferredHotelPickerModal` (the in-house hotel picker): lists
 * ACTIVE cruises homeported in the stay's embark city, prices every cabin
 * against the itinerary's configured rooms via the shared cabin-pricing
 * engine (a thin adapter over the hotel room-pricing engine - see
 * `@utazzo/shared/utils/cruise-pricing`), and on pick emits a `source: 'DB'`
 * CRUISE component that replaces the one already on the day (swap-in-place,
 * via `replaceLocal` in `DaysTab`).
 *
 * Two entry points, mirroring the hotel card's "Change hotel" (full list) vs
 * "Change Rooms & Meals" (current hotel only):
 *  - `lockedCruiseId` unset -> "Change cruise": lists every ACTIVE cruise
 *    homeported in the embark city, cabins collapsed.
 *  - `lockedCruiseId` set -> "Change cabin & meals": the list is filtered
 *    down to that one cruise (already selected on the day) with its cabins
 *    expanded by default, so the operator only picks a different cabin/meal
 *    plan on the SAME cruise, not a different ship.
 *
 * The `metaJson` shape on pick matches `buildCruiseProposal`'s `baseMeta`
 * (apps/api/src/modules/itineraries/auto-search.service.ts) key-for-key -
 * shipName/cabinName/deck/boardBasis/nights/checkIn/checkOut/occupancy/
 * currencyCode - so a manually-swapped cruise renders identically to an
 * auto-selected one on the day card (`CruiseCard` in `ComponentCard.tsx`),
 * and survives a later Resync/reconcile (`loadExistingCruiseByStay` keeps a
 * cruise/cabin already on its stay in place).
 */

interface CruiseConfigRoom {
  adults: number;
  children: number;
  childrenNoBed: number;
  infants: number;
}

interface CruiseChangeModalProps {
  /** Itinerary detail is fetched here (same as the hotel modal) to derive
   *  the configured rooms/occupancy - the SAME rooms every hotel/cruise
   *  stay in this itinerary prices against. */
  itineraryId: string;
  /** Embark city - cruises are homeported here. */
  cityId: string | null;
  cityName: string | null;
  dayId: string | null;
  /** Embark date, 'YYYY-MM-DD'. */
  checkIn: string | null;
  /** Nights aboard (embark -> disembark). */
  nights: number;
  currency: string;
  /** When set, picking a cabin REPLACES this draft component (swap-in-place). */
  replaceId?: string | null;
  /** When set, this is "Change cabin & meals" for the cruise already on the
   *  day - the list is filtered to just this cruise id (cabins expanded by
   *  default) instead of every ACTIVE cruise in the embark city. */
  lockedCruiseId?: string | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateItineraryComponentRequest) => void | Promise<void>;
}

/** "YYYY-MM-DD" + n days -> "YYYY-MM-DD" (UTC-consistent, no IST off-by-one).
 *  Same helper as PreferredHotelPickerModal's `addDay`. */
const addDay = (isoDate: string, n: number): string => {
  const d = new Date(isoDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** A cabin rate card applies on check-in when the date falls in its validity
 *  window, isn't blacked out, and the date's weekday bit is set in the mask
 *  (bit0 = Sun .. bit6 = Sat). Mirrors `isCruiseRateValidOn` in
 *  auto-search.service.ts exactly (that engine only checks the EMBARK date,
 *  not every night aboard - the list endpoint's `onDate` filter only checks
 *  the validity window, so weekday + blackout are re-checked here). */
const isRateValidOnCheckIn = (rate: CruiseCabinRate, checkIn: string): boolean => {
  if (checkIn < rate.validityFrom || checkIn > rate.validityTo) return false;
  if (rate.blackoutDates.includes(checkIn)) return false;
  const bit = 1 << new Date(checkIn + 'T00:00:00Z').getUTCDay();
  return (rate.weekdayMask & bit) !== 0;
};

export const CruiseChangeModal = ({
  itineraryId,
  cityId,
  cityName,
  checkIn,
  nights,
  currency,
  lockedCruiseId,
  pending,
  onClose,
  onSubmit,
}: CruiseChangeModalProps): React.JSX.Element => {
  const detail = useItineraryDetail(itineraryId).data ?? null;
  // The configured rooms + their occupancy - what the pricing engine prices.
  // Derived from the itinerary detail the SAME way PreferredHotelPickerModal
  // does (every stay in the trip - hotel or cruise - prices the same rooms).
  const configRooms = useMemo<CruiseConfigRoom[]>(() => {
    const rooms = detail?.roomGroups?.flatMap((g) => g.rooms) ?? [];
    if (rooms.length === 0) return [{ adults: 2, children: 0, childrenNoBed: 0, infants: 0 }];
    return rooms.map((r) => ({
      adults: Math.max(1, r.adults),
      children: r.children,
      childrenNoBed: Math.min(r.childrenNoBed ?? 0, r.children),
      infants: r.infants,
    }));
  }, [detail?.roomGroups]);
  const roomsCount = configRooms.length;

  // "Change cabin & meals" opens with that cruise's cabins already expanded -
  // there's nothing else to pick from, so skip the extra tap.
  const [expandedId, setExpandedId] = useState<string | null>(lockedCruiseId ?? null);

  const cruisesQuery = useCruisesList({
    page: 1,
    pageSize: 50,
    sortBy: 'name',
    sortDir: 'asc',
    status: 'ACTIVE',
    ...(cityId ? { cityId } : {}),
  });
  const cruises: CruiseListItem[] = useMemo(
    () => cruisesQuery.data?.data ?? [],
    [cruisesQuery.data],
  );
  // "Change cabin & meals": narrow the list down to the cruise already on the
  // day (a different ship is a "Change cruise" action, not this one).
  const visibleCruises = useMemo(
    () => (lockedCruiseId ? cruises.filter((c) => c.id === lockedCruiseId) : cruises),
    [cruises, lockedCruiseId],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <Ship className="h-4 w-4 text-cyan-600" aria-hidden />
            <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
              {lockedCruiseId ? 'Change cabin & meals' : 'Change cruise'}
            </h3>
            {!lockedCruiseId && cityName && (
              <span className="text-xs text-zinc-500">Homeported in {cityName}</span>
            )}
            {checkIn && (
              <span className="text-xs text-zinc-400">
                Check-in {checkIn} · {nights}N · {roomsCount} cabin{roomsCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {!cityId ? (
            <div className="py-10 text-center text-sm text-zinc-400">
              This stay has no embark city set - fix the itinerary's cruise leg first.
            </div>
          ) : cruisesQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-400">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading cruises...
            </div>
          ) : cruises.length === 0 ? (
            <div className="py-10 text-center text-sm text-zinc-400">
              No ACTIVE cruises homeported {cityName ? `in ${cityName}` : 'in this city'}. Add one
              in the Cruises master first.
            </div>
          ) : visibleCruises.length === 0 ? (
            <div className="py-10 text-center text-sm text-zinc-400">
              Could not find the current cruise - it may no longer be ACTIVE. Use "Change cruise" to
              pick a different one instead.
            </div>
          ) : (
            <ul className="space-y-2">
              {visibleCruises.map((cruise) => (
                <CruiseResultCard
                  key={cruise.id}
                  cruise={cruise}
                  currency={currency}
                  nights={nights}
                  roomsCount={roomsCount}
                  configRooms={configRooms}
                  checkIn={checkIn}
                  expanded={expandedId === cruise.id}
                  pending={pending}
                  onToggle={() => setExpandedId(expandedId === cruise.id ? null : cruise.id)}
                  onPick={onSubmit}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

interface CruiseResultCardProps {
  cruise: CruiseListItem;
  currency: string;
  nights: number;
  roomsCount: number;
  configRooms: CruiseConfigRoom[];
  checkIn: string | null;
  expanded: boolean;
  pending: boolean;
  onToggle: () => void;
  onPick: (payload: CreateItineraryComponentRequest) => void | Promise<void>;
}

const CruiseResultCard = ({
  cruise,
  currency,
  nights,
  roomsCount,
  configRooms,
  checkIn,
  expanded,
  pending,
  onToggle,
  onPick,
}: CruiseResultCardProps): React.JSX.Element => {
  // Cabins + rate cards fetched lazily, only when the card is expanded - same
  // convention as the hotel picker's rooms/inventory.
  const coverUrl = useCruiseCoverUrl(cruise.photos);
  const cabinsQuery = useCruiseCabins(expanded ? cruise.id : null);
  const ratesQuery = useCruiseRates(expanded ? cruise.id : null, {
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    ...(checkIn ? { onDate: checkIn } : {}),
  });

  const cabins = useMemo(
    () => (cabinsQuery.data ?? []).filter((c) => c.isActive),
    [cabinsQuery.data],
  );
  const ratesByCabin = useMemo(() => {
    const map = new Map<string, CruiseCabinRate[]>();
    for (const rate of ratesQuery.data?.data ?? []) {
      if (!rate.cruiseCabinId) continue;
      const list = map.get(rate.cruiseCabinId) ?? [];
      list.push(rate);
      map.set(rate.cruiseCabinId, list);
    }
    return map;
  }, [ratesQuery.data]);

  // Rate cards carry their own currency (per rate row); every cabin on a
  // given cruise realistically shares one, so - like the net-price fx
  // preview in DayItemPickerModal - a single live rate is fetched for the
  // first rate seen and reused for every cabin's conversion below.
  const sourceCurrency = ratesQuery.data?.data[0]?.currencyCode ?? currency;
  const fx = useFxRate(sourceCurrency, currency);
  const fxRate = fx.data?.rate ?? 1;

  // Price the FULL room config against this cabin via the shared cabin
  // engine (fixed-cost or per-pax, occupancy-aware) - mirrors
  // `buildCruiseProposal` in auto-search.service.ts: filter to rate cards
  // valid on embark day, then for each configured room take the cheapest
  // valid rate, summing across rooms.
  const priceCabin = (
    cabin: CruiseCabin,
  ): { total: number; unavailable: string | null; boardBasis: string | null } => {
    if (!checkIn) return { total: 0, unavailable: 'No embark date set', boardBasis: null };
    const rates = ratesByCabin.get(cabin.id) ?? [];
    const validRates = rates.filter((r) => isRateValidOnCheckIn(r, checkIn));
    if (validRates.length === 0) {
      return { total: 0, unavailable: 'No valid rate card for these dates', boardBasis: null };
    }
    const boardBasis = validRates[0]?.mealPlanName ?? cabin.mealPlanCode ?? null;
    // Occupancy-tiered pricing (mirrors buildCruiseProposal): a cabin whose
    // valid rates span >1 distinct occupancy (Single/Double/Triple fixed cabin
    // costs) is priced by an EXACT adult-count match, with children charged on
    // top (no free-child allowance). A single-tier cabin keeps the legacy
    // cheapest-fitting behaviour. Non-matching tiered cabins show disabled.
    const occOf = (r: CruiseCabinRate): number | null =>
      r.baseOccupancy ?? r.occupancyPaxCount ?? null;
    const occTiers = new Set(validRates.map(occOf).filter((n): n is number => n !== null));
    const tiered = occTiers.size > 1;
    let total = 0;
    for (const room of configRooms) {
      const eligible = tiered ? validRates.filter((r) => occOf(r) === room.adults) : validRates;
      if (tiered && eligible.length === 0) {
        return {
          total: 0,
          unavailable: `This cabin has no rate for ${room.adults} adult${room.adults === 1 ? '' : 's'}`,
          boardBasis,
        };
      }
      let roomBest: number | null = null;
      for (const rate of eligible) {
        const rateInput = cabinRateToRateInput(
          {
            fixedCabinCost: rate.fixedCabinCost,
            adultPrice: rate.adultPrice,
            childWithBedPrice: rate.childWithBedPrice,
            childWithoutBedPrice: rate.childWithoutBedPrice,
            infantPrice: rate.infantPrice,
            extraAdultPrice: rate.extraAdultPrice,
            baseOccupancy: rate.baseOccupancy ?? rate.occupancyPaxCount ?? 2,
          },
          cabin.maxOccupancy,
        );
        // Tiered fixed cost covers exactly its adults with no child bundled.
        const input = tiered ? { ...rateInput, baseChildrenIncluded: 0 } : rateInput;
        const nightlyRates = new Array(Math.max(1, nights)).fill(input);
        const priced = priceCabinStay(nightlyRates, {
          adults: room.adults,
          children: room.children,
          childrenNoBed: room.childrenNoBed,
          infants: room.infants,
        });
        if (priced.unavailableReason) continue;
        if (roomBest === null || priced.amount < roomBest) roomBest = priced.amount;
      }
      if (roomBest === null) {
        return {
          total: 0,
          unavailable: 'Exceeds cabin max occupancy for these travellers',
          boardBasis,
        };
      }
      total += roomBest;
    }
    return { total, unavailable: null, boardBasis };
  };

  const pick = (cabin: CruiseCabin): void => {
    if (!checkIn) return;
    const { total, unavailable, boardBasis } = priceCabin(cabin);
    if (unavailable) return; // can't quote an impossible cabin
    const checkOut = addDay(checkIn, nights);
    const costPaise = String(Math.round(total * fxRate * 100));

    void onPick({
      kind: 'CRUISE',
      source: 'DB',
      title: `${cruise.name} - ${cabin.name}`,
      cruiseId: cruise.id,
      cruiseCabinId: cabin.id,
      costPaise,
      markupPaise: '0',
      isRefundable: false,
      startDate: checkIn,
      endDate: checkOut,
      metaJson: {
        // Same key names buildCruiseProposal's baseMeta writes, so a manual
        // swap renders identically to an auto-selected cruise on the day
        // card (CruiseCard in ComponentCard.tsx).
        shipName: cruise.name,
        cabinName: cabin.name,
        deck: cabin.deck,
        boardBasis,
        nights,
        checkIn,
        checkOut,
        occupancy: configRooms,
        // Already converted to the itinerary currency above (like
        // buildCruiseProposal) - stamping the target code means
        // ComponentsService.create's FX step sees source === target and
        // no-ops, so this never double-converts.
        currencyCode: currency,
      },
    });
  };

  return (
    <li className="list-none rounded-xl border border-zinc-200 bg-white p-3.5 transition-shadow hover:shadow-sm">
      <div className="flex items-start gap-3">
        <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-linear-to-br from-cyan-100 to-cyan-50">
          {coverUrl ? (
            <img src={coverUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <Ship className="h-6 w-6 text-cyan-400" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-zinc-900">{cruise.name}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-zinc-500">
            {cruise.cruiseLine && <span>{cruise.cruiseLine}</span>}
            {cruise.starRating > 0 && <UzBadge tone="secondary">{cruise.starRating} star</UzBadge>}
            <UzBadge tone="secondary">
              {cruise.cabinCount} cabin{cruise.cabinCount === 1 ? '' : 's'}
            </UzBadge>
          </div>
        </div>
        <UzButton size="sm" variant="outline" className="h-7 shrink-0 text-xs" onClick={onToggle}>
          {expanded ? 'Hide cabins' : 'Select cabin'}
          <ChevronDown
            className={'ml-1 h-3 w-3 transition-transform ' + (expanded ? 'rotate-180' : '')}
            aria-hidden
          />
        </UzButton>
      </div>

      {expanded && (
        <div className="mt-2.5 space-y-1.5 rounded-xl border border-zinc-200 bg-zinc-50/60 p-3">
          {cabinsQuery.isLoading || ratesQuery.isLoading ? (
            <div className="flex items-center gap-2 py-2 text-xs text-zinc-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Fetching cabins...
            </div>
          ) : cabins.length === 0 ? (
            <p className="py-2 text-xs text-zinc-500">No cabins configured for this cruise.</p>
          ) : (
            cabins.map((cabin) => {
              const { total, unavailable, boardBasis } = priceCabin(cabin);
              const totalInr = total * fxRate;
              return (
                <div
                  key={cabin.id}
                  className="flex flex-col gap-2 rounded-lg border border-zinc-200 bg-white p-2.5"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium break-words text-zinc-900">{cabin.name}</p>
                      <div className="mt-0.5 flex flex-wrap gap-1.5">
                        {cabin.deck && <UzBadge tone="secondary">{cabin.deck}</UzBadge>}
                        {cabin.occupancyTypeName && (
                          <UzBadge tone="secondary">{cabin.occupancyTypeName}</UzBadge>
                        )}
                        {boardBasis && <MealBadge mealType={boardBasis} />}
                        <UzBadge tone="secondary">max {cabin.maxOccupancy} pax</UzBadge>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end">
                      {unavailable ? (
                        <span className="flex items-center gap-1 text-[11px] text-zinc-400">
                          <AlertTriangle className="h-3 w-3" aria-hidden />
                          {unavailable}
                        </span>
                      ) : (
                        <>
                          <AdminOnlyAmount
                            costPaise={String(Math.round(totalInr * 100))}
                            currency={currency}
                            className="text-xs font-semibold text-zinc-900 tabular-nums"
                          />
                          <span className="text-[10px] text-zinc-500">
                            {nights}N × {roomsCount} cabin{roomsCount === 1 ? '' : 's'}
                          </span>
                        </>
                      )}
                    </div>
                    <UzButton
                      size="sm"
                      variant="outline"
                      className="h-7 shrink-0 text-xs"
                      loading={pending}
                      disabled={Boolean(unavailable)}
                      onClick={() => pick(cabin)}
                    >
                      Select
                    </UzButton>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </li>
  );
};
